import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  AddressLocationReviewExportResultType,
  EXPORT_ADDRESS_LOCATION_REVIEWS,
  ExportAddressLocationReviewsResponse,
  ExportAddressLocationReviewsVariables,
} from '../graphql/mutations/exportAddressLocationReviews';
import { convertToGraphQLFilters, FilterOptions } from '../components/FilterArea';
import { translateGraphQLError } from '../../../../utils/graphqlUtils';

const I18N_PREFIX = 'mapTool.addressLibrary.locationCorrectionPage.export';

export type ExportOutcome =
  | { kind: 'download'; downloadUrl: string; recordCount: number }
  | { kind: 'warn'; messageKey: string }
  | { kind: 'error'; messageKey: string };

/**
 * 把后端结果码映射为前端动作：成功 → 下载；无数据 / 超限 → 提示；其它 → 错误。
 * 纯函数，便于单测。
 */
export const resolveExportOutcome = (result: AddressLocationReviewExportResultType | null | undefined): ExportOutcome => {
  if (result?.success && result.downloadUrl) {
    return { kind: 'download', downloadUrl: result.downloadUrl, recordCount: result.recordCount ?? 0 };
  }
  switch (result?.code) {
    case 'NO_DATA':
      return { kind: 'warn', messageKey: `${I18N_PREFIX}.noData` };
    case 'EXCEED_MAX_LIMIT':
      return { kind: 'warn', messageKey: `${I18N_PREFIX}.exceedMaxLimit` };
    default:
      return { kind: 'error', messageKey: `${I18N_PREFIX}.failed` };
  }
};

/**
 * 预签名 URL → blob → <a download> 触发浏览器下载（与邮编导出 ZipcodesPage 同写法）
 */
export const downloadFileFromUrl = async (downloadUrl: string, fileName: string): Promise<void> => {
  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error(`download failed: ${response.status}`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

/**
 * 请求抛错时的提示文案：后端业务异常（如下载锁冲突 errors.business.download.duplicateRequest）
 * 带 extensions.messageKey 且有译文时显示专门文案，否则回落通用「导出失败」。
 */
export const resolveExportErrorMessage = (error: unknown): string =>
  translateGraphQLError(error, `${I18N_PREFIX}.failed`);

export const buildExportFileName = (now: Date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `address_location_review_export_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xlsx`;
};

/**
 * 位置纠偏列表「下载」：用与列表相同的筛选条件请求导出，拿到下载 URL 后直接触发浏览器下载。
 */
export const useAddressLocationReviewExport = () => {
  const { t, i18n } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [exportMutation] = useMutation<ExportAddressLocationReviewsResponse, ExportAddressLocationReviewsVariables>(
    EXPORT_ADDRESS_LOCATION_REVIEWS
  );

  const exportByFilters = useCallback(
    async (options: FilterOptions) => {
      setExporting(true);
      try {
        const { data } = await exportMutation({
          variables: {
            filters: convertToGraphQLFilters(options),
            language: i18n.resolvedLanguage || i18n.language,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        });
        const outcome = resolveExportOutcome(data?.exportAddressLocationReviews);
        if (outcome.kind === 'download') {
          await downloadFileFromUrl(outcome.downloadUrl, buildExportFileName());
          message.success(t(`${I18N_PREFIX}.success`, { count: outcome.recordCount }) as string);
        } else if (outcome.kind === 'warn') {
          message.warning(t(outcome.messageKey) as string);
        } else {
          message.error(t(outcome.messageKey) as string);
        }
      } catch (error) {
        console.error('Export address location reviews error:', error);
        message.error(resolveExportErrorMessage(error));
      } finally {
        setExporting(false);
      }
    },
    [exportMutation, i18n, t]
  );

  return { exportByFilters, exporting };
};
